/**
 * LoginPage — 登录页（独立路由）
 * 视觉风格与 /login 统一
 */
import { Typography, message } from 'antd';
import { Form, Input, Button } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { MailOutlined, LockOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { useAuthStore } from '../../stores/auth';
import FireflyField from '../../components/FireflyField';

const { Text } = Typography;

export default function LoginPage() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const loading = useAuthStore((s) => s.loading);

  const onFinish = async (values: { email: string; password: string }) => {
    try {
      await login({
        email: values.email,
        password: values.password,
        loginType: 'PASSWORD',
      });
      message.success('登录成功');
      navigate('/');
    } catch (err: any) {
      message.error(err?.message || '登录失败，请重试');
    }
  };

  return (
    <div style={{
      minHeight: '100dvh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* 背景 */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: 'url(/hero-deer-forest.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        zIndex: 0,
      }} />
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(90deg, rgba(4,15,34,0.38) 0%, rgba(4,15,34,0.22) 40%, rgba(4,15,34,0.08) 70%, transparent 100%)',
        zIndex: 1,
      }} />
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(to bottom, transparent 60%, rgba(7,21,45,0.15) 80%, rgba(7,21,45,0.35) 100%)',
        zIndex: 1,
      }} />

      <FireflyField count={5} area="default" />

      {/* 登录面板 */}
      <div className="auth-login-panel" style={{
        position: 'relative',
        zIndex: 10,
        width: 440,
        maxWidth: '90vw',
        marginLeft: '8vw',
        padding: '44px 40px',
        background: 'rgba(8, 22, 40, 0.62)',
        borderRadius: 24,
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
        border: '1px solid rgba(220,235,255,0.12)',
        boxShadow: '0 24px 80px rgba(0,0,0,0.25)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <img src="/logo-cat-white.png" alt="Simon Cat Agent" style={{ width: 40, height: 40, marginBottom: 16 }} />
          <div style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 20,
            fontWeight: 400,
            color: 'var(--text-primary)',
            marginBottom: 4,
          }}>
            回到你的创作空间
          </div>
          <Text style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
            AI 陪伴创作，让灵感发光
          </Text>
        </div>

        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            label="邮箱"
            name="email"
            rules={[
              { required: true, message: '请输入邮箱' },
              { type: 'email', message: '请输入正确的邮箱格式' },
            ]}
          >
            <Input
              prefix={<MailOutlined style={{ color: 'rgba(220,235,255,0.42)' }} />}
              placeholder="请输入邮箱"
              style={{
                height: 52,
                background: 'rgba(255,255,255,0.045)',
                border: '1px solid rgba(220,235,255,0.10)',
                borderRadius: 14,
                color: 'var(--text-primary)',
                fontSize: 14,
              }}
            />
          </Form.Item>
          <Form.Item
            label="密码"
            name="password"
            rules={[{ required: true, message: '请输入密码' }]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: 'rgba(220,235,255,0.42)' }} />}
              placeholder="请输入密码"
              style={{
                height: 52,
                background: 'rgba(255,255,255,0.045)',
                border: '1px solid rgba(220,235,255,0.10)',
                borderRadius: 14,
                color: 'var(--text-primary)',
                fontSize: 14,
              }}
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={loading}
              style={{
                height: 52,
                background: 'linear-gradient(135deg, #F2D87E 0%, #DDB94F 100%)',
                border: 'none',
                borderRadius: 14,
                fontSize: 15,
                fontWeight: 600,
                color: '#08162A',
                boxShadow: '0 4px 20px rgba(235,216,126,0.18)',
              }}
            >
              进入我的创作空间 <ArrowRightOutlined style={{ fontSize: 12, marginLeft: 4 }} />
            </Button>
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Link
              to="/auth/forgot"
              style={{ color: '#E9D38A', fontSize: 13, fontFamily: 'var(--font-sans)', opacity: 0.85 }}
            >
              忘记密码？
            </Link>
            <Link
              to="/auth/register"
              style={{ color: 'var(--text-secondary)', fontSize: 13, fontFamily: 'var(--font-sans)' }}
            >
              注册账号
            </Link>
          </div>
        </Form>

        <div style={{
          textAlign: 'center',
          marginTop: 28,
          color: 'rgba(220,235,255,0.30)',
          fontSize: 12,
          fontFamily: 'var(--font-sans)',
        }}>
          © 2026 西蒙的猫 · 萤火秘境
        </div>
      </div>

      <style>{`
        .ant-input,
        .ant-input-password,
        .ant-input-affix-wrapper {
          background: rgba(255,255,255,0.045) !important;
          border-color: rgba(220,235,255,0.10) !important;
          color: var(--text-primary) !important;
        }
        .ant-input-password input,
        .ant-input-affix-wrapper input {
          background: transparent !important;
          color: var(--text-primary) !important;
        }
        .ant-input::placeholder { color: rgba(220,235,255,0.38) !important; }
        .ant-input:hover { border-color: rgba(220,235,255,0.18) !important; }
        .ant-input:focus, .ant-input-focused,
        .ant-input-password:focus-within,
        .ant-input-affix-wrapper-focused {
          border-color: rgba(235,220,155,0.45) !important;
          box-shadow: 0 0 0 3px rgba(235,220,155,0.06) !important;
        }
        .ant-form-item > .ant-form-item-label > label {
          color: rgba(220,235,255,0.65) !important;
          font-size: 13px !important;
        }
        .ant-form-item-explain-error {
          color: rgba(255,140,120,0.85) !important;
          font-size: 12px !important;
        }
        @media (max-width: 768px) {
          .auth-login-panel {
            margin-left: auto !important;
            margin-right: auto !important;
            padding: 36px 24px !important;
          }
        }
        @media (max-width: 480px) {
          .auth-login-panel {
            margin-left: 12px !important;
            margin-right: 12px !important;
            width: calc(100% - 24px) !important;
            max-width: 100% !important;
            padding: 28px 20px !important;
          }
        }
      `}</style>
    </div>
  );
}
